/**
 * useToast — Toast context + consumer hook
 *
 * Split out of Toast.tsx (E.14, 2026-04-29) so that Toast.tsx is
 * component-only and Vite Fast Refresh works without
 * react-refresh/only-export-components warnings.
 *
 * Toast.tsx imports ToastContext from this file; consumers import
 * useToast from this file (or from the components barrel).
 */
import { createContext, useContext } from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration: number;
}

export interface ToastContextValue {
  toasts: Toast[];
  // Phase B (E.21.37) Reviewer 2 Concern 1: returns the new toast's ID so
  // callers can dismiss explicitly via `removeToast(id)` before the
  // natural duration expires. Pre-fix this was `=> void`, which forced
  // the only caller (useAutoRebuildOnSave) to dismiss-by-undefined.
  addToast: (type: ToastType, message: string, duration?: number) => string;
  removeToast: (id: string) => void;
}

export const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }

  return {
    success: (message: string, duration?: number): string =>
      context.addToast('success', message, duration),
    error: (message: string, duration?: number): string =>
      context.addToast('error', message, duration),
    warning: (message: string, duration?: number): string =>
      context.addToast('warning', message, duration),
    info: (message: string, duration?: number): string =>
      context.addToast('info', message, duration),
    dismiss: context.removeToast,
  };
}
